import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
const initialTestimonials = {
    doula: [
        {
            id: '1',
            name: 'Sarah M.',
            initials: 'S.M.',
            content: 'Madison\'s presence during my birth was absolutely transformative. She held space with such reverence and trust in my body\'s wisdom. I felt completely supported and empowered throughout my entire labor.',
            service: 'doula',
            visible: true
        },
        {
            id: '2',
            name: 'Emily R.',
            initials: 'E.R.',
            content: 'Having Madison as my doula was like having a wise, loving sister by my side. She knew exactly when to offer support and when to step back. My birth experience was exactly what I needed.',
            service: 'doula',
            visible: true
        },
        {
            id: '3',
            name: 'T.C.',
            initials: 'T.C.',
            content: 'Madison is a goddess of a doula who goes above and beyond. She helped me realize my own strength, and I am forever grateful for her contribution to my birth. Because of her my delivery and aftercare was nothing short of magical!',
            service: 'doula',
            visible: true
        }
    ],
    sacredMotherhood: [
        {
            id: '1',
            name: 'Jennifer L.',
            initials: 'J.L.',
            content: 'The Sacred Motherhood Journey program completely changed my perspective on birth and motherhood. I felt so prepared and connected to my intuition. Madison\'s guidance was gentle yet profound.',
            service: 'sacredMotherhood',
            visible: true
        },
        {
            id: '2',
            name: 'Amanda R.',
            initials: 'A.R.',
            content: 'Madison\'s Sacred Motherhood Journey program completely changed my perspective on birth and motherhood. I felt so empowered and prepared for my birth experience.',
            service: 'sacredMotherhood',
            visible: true
        }
    ]
};
const initialResources = [
    {
        id: "digital-downloads",
        title: "Digital Downloads",
        description: "Guides, meditations, and practices to support your journey",
        icon: "/assets/icons/Downloads.svg",
        visible: true,
        items: [
            {
                id: "sacred-motherhood-guide",
                name: "Sacred Motherhood Starter Guide",
                description: "A gentle introduction to sacred motherhood practices",
                price: "$7",
                status: "coming-soon",
                image: "/assets/SERVICE_Sacred1.png",
                category: "digital-downloads",
                visible: true
            },
            {
                id: "prenatal-yoga-sequence",
                name: "Prenatal Yoga Sequence",
                description: "Complete 30-minute prenatal yoga flow with modifications",
                price: "$5",
                status: "coming-soon",
                image: "/assets/SERVICE_doula.png",
                category: "digital-downloads",
                visible: false
            },
            {
                id: "reiki-self-care",
                name: "Reiki Self-Care Ritual",
                description: "Learn simple energy healing techniques for daily use",
                price: "$8",
                status: "coming-soon",
                image: "/assets/SERVICE_womensCircle.png",
                category: "digital-downloads",
                visible: false
            }
        ]
    },
    {
        id: "printables",
        title: "Printable Resources",
        description: "Journals, planners, and affirmation cards",
        icon: "/assets/icons/print.svg",
        visible: true,
        items: [
            {
                id: "postpartum-journal",
                name: "Postpartum Wellness Journal",
                description: "30-day guided journal for postpartum healing",
                price: "$12",
                status: "coming-soon",
                image: "/assets/SERVICE_Sacred1.png",
                category: "printables",
                visible: false
            },
            {
                id: "affirmation-cards",
                name: "Sacred Motherhood Affirmation Cards",
                description: "Printable cards with daily affirmations",
                price: "$6",
                status: "coming-soon",
                image: "/assets/SERVICE_doula.png",
                category: "printables",
                visible: true
            }
        ]
    },
    {
        id: "bundles",
        title: "Complete Bundles",
        description: "Curated collections for specific needs",
        icon: "🎁",
        visible: true,
        items: [
            {
                id: "birth-preparation-bundle",
                name: "Birth Preparation Bundle",
                description: "Complete guide with meditations, yoga, and affirmations",
                price: "$25",
                status: "coming-soon",
                image: "/assets/SERVICE_womensCircle.png",
                category: "bundles",
                visible: false
            },
            {
                id: "sacred-motherhood-kit",
                name: "Sacred Motherhood Complete Kit",
                description: "Everything you need for your sacred motherhood journey",
                price: "$35",
                status: "coming-soon",
                image: "/assets/SERVICE_Sacred1.png",
                category: "bundles",
                visible: false
            }
        ]
    },
    {
        id: "recommended-products",
        title: "Recommended Products",
        description: "Tools and resources I trust and use",
        icon: "🛍️",
        visible: true,
        items: [
            {
                id: "yoga-props",
                name: "Yoga Props & Equipment",
                description: "Essential items for prenatal and postnatal practice",
                price: "Varies",
                status: "affiliate",
                image: "/assets/SERVICE_doula.png",
                category: "recommended-products",
                visible: false
            },
            {
                id: "wellness-products",
                name: "Natural Wellness Products",
                description: "Organic teas, essential oils, and healing tools",
                price: "Varies",
                status: "affiliate",
                image: "/assets/SERVICE_womensCircle.png",
                category: "recommended-products",
                visible: false
            }
        ]
    }
];
const initialContentState = {
    services: [
        { id: 'doula', name: 'Doula Services', path: '/doula', visible: true, adminOnly: false, description: 'Compassionate, hands-on support for home birth and midwife-attended hospital birth, from pregnancy through postpartum.' },
        { id: 'sacred-motherhood', name: 'Sacred Motherhood Journey', path: '/sacred-motherhood', visible: false, adminOnly: false, description: 'A five-session immersion, weaving energy balancing, embodiment, and holistic wellness to ready your body, mind, and spirit for the path ahead.' },
        { id: 'gallery', name: 'Birth Gallery', path: '/gallery', visible: true, adminOnly: false, description: 'the sacred story of birth, photographed with love.' },
        { id: 'womens-circles', name: 'Women\'s Circles', path: '/womens-circles', visible: false, adminOnly: false, description: 'Sacred spaces for women to connect and grow.' },
    ],
    resources: initialResources,
    testimonials: initialTestimonials,
    supportButtons: {
        visible: false
    }
};
const ContentContext = createContext(undefined);
export const useContent = () => {
    const context = useContext(ContentContext);
    if (context === undefined) {
        throw new Error('useContent must be used within a ContentProvider');
    }
    return context;
};
export const ContentProvider = ({ children }) => {
    // Content version for cache busting - increment when schema changes
    const CONTENT_VERSION = '1.2';
    const VERSION_KEY = 'contentVersion';
    const [content, setContent] = useState(() => {
        var _a;
        const savedVersion = localStorage.getItem(VERSION_KEY);
        const saved = localStorage.getItem('content');
        // Clear cache if version mismatch
        if (savedVersion !== CONTENT_VERSION) {
            localStorage.removeItem('content');
            localStorage.setItem(VERSION_KEY, CONTENT_VERSION);
            return initialContentState;
        }
        // Also check if sacred-motherhood is still visible in cache (legacy check)
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                const sacredMotherhoodService = (_a = parsed === null || parsed === void 0 ? void 0 : parsed.services) === null || _a === void 0 ? void 0 : _a.find((s) => s.id === 'sacred-motherhood');
                if (sacredMotherhoodService && sacredMotherhoodService.visible === true) {
                    localStorage.removeItem('content');
                    return initialContentState;
                }
            }
            catch (e) {
                // If parsing fails, clear cache
                localStorage.removeItem('content');
                return initialContentState;
            }
        }
        const parsed = saved ? JSON.parse(saved) : initialContentState;
        // Ensure all required properties exist with fallbacks
        // Force services to use initialContentState visible flags to prevent stale cache
        const mergedContent = Object.assign(Object.assign(Object.assign({}, initialContentState), parsed), { services: initialContentState.services.map(initialService => {
                var _a;
                const cachedService = (_a = parsed.services) === null || _a === void 0 ? void 0 : _a.find((s) => s.id === initialService.id);
                // Use initialContentState visible flag, but keep other cached properties
                return cachedService ? Object.assign(Object.assign({}, cachedService), { visible: initialService.visible }) : initialService;
            }), supportButtons: Object.assign({ visible: false }, parsed.supportButtons) });
        return mergedContent;
    });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    useEffect(() => {
        localStorage.setItem('content', JSON.stringify(content));
        localStorage.setItem(VERSION_KEY, CONTENT_VERSION);
    }, [content]);
    const updateContent = (updates) => {
        setContent(prev => (Object.assign(Object.assign({}, prev), updates)));
    };
    const addTestimonial = (service, testimonial) => {
        const newTestimonial = Object.assign(Object.assign({}, testimonial), { id: Date.now().toString(), service });
        setContent(prev => (Object.assign(Object.assign({}, prev), { testimonials: Object.assign(Object.assign({}, prev.testimonials), { [service]: [...(prev.testimonials[service] || []), newTestimonial] }) })));
    };
    const removeTestimonial = (service, testimonialId) => {
        setContent(prev => (Object.assign(Object.assign({}, prev), { testimonials: Object.assign(Object.assign({}, prev.testimonials), { [service]: (prev.testimonials[service] || []).filter(t => t.id !== testimonialId) }) })));
    };
    const toggleTestimonialVisibility = (service, testimonialId) => {
        setContent(prev => (Object.assign(Object.assign({}, prev), { testimonials: Object.assign(Object.assign({}, prev.testimonials), { [service]: (prev.testimonials[service] || []).map(t => t.id === testimonialId ? Object.assign(Object.assign({}, t), { visible: !t.visible }) : t) }) })));
    };
    const toggleResourceVisibility = (categoryId, resourceId) => {
        setContent(prev => (Object.assign(Object.assign({}, prev), { resources: prev.resources.map(category => category.id === categoryId
                ? Object.assign(Object.assign({}, category), { items: category.items.map(item => item.id === resourceId
                        ? Object.assign(Object.assign({}, item), { visible: !item.visible }) : item) }) : category) })));
    };
    const toggleResourceCategoryVisibility = (categoryId) => {
        setContent(prev => (Object.assign(Object.assign({}, prev), { resources: prev.resources.map(category => category.id === categoryId
                ? Object.assign(Object.assign({}, category), { visible: !category.visible }) : category) })));
    };
    const login = (password) => {
        if (password === 'admin123') {
            setIsAuthenticated(true);
            return true;
        }
        return false;
    };
    const value = {
        content,
        updateContent,
        addTestimonial,
        removeTestimonial,
        toggleTestimonialVisibility,
        toggleResourceVisibility,
        toggleResourceCategoryVisibility,
        login,
        isAuthenticated
    };
    return (_jsx(ContentContext.Provider, { value: value, children: children }));
};
